import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SwitchTransition, Transition } from 'react-transition-group'

import Button from '@/components/Button/Button'
import Pagination from '@/components/Chat/Pagination'
import { useGSAP } from '@/composables/useGSAP'

import QuestionsLikertInstruction from './QuestionsLikertInstruction'
import QuestionsLikertNumber from './QuestionsLikertNumber'

export type QuestionsLikertProps = {
  className?: string
  index: number
  max: number
  question: string
  onChoose?: (choice: string) => void
  isVisible: boolean
}

export default function QuestionsLikert({
  index,
  max,
  className,
  question,
  onChoose,
  isVisible,
}: QuestionsLikertProps) {
  const { t } = useTranslation()
  const { gsap } = useGSAP()

  const [value, setValue] = useState<number>(2)
  const [isBackgroundEntering, setIsBackgroundEntering] = useState<boolean>(true)

  useEffect(() => {
    setValue(2)
  }, [index, setValue])

  const onNext = useCallback(() => {
    onChoose?.(`${value + 1}`)
  }, [onChoose, value])

  const elementRef = useRef<HTMLDivElement>(null)
  const container = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLDivElement>(null)
  const instructionRef = useRef<HTMLDivElement>(null)
  const numberRef = useRef<HTMLDivElement>(null)

  const onEnter = useCallback(() => {
    const timeline = gsap.timeline({ delay: 0.4 }).fromTo(
      [container.current, buttonRef.current],
      {
        opacity: 0,
      },
      {
        opacity: 1,
        duration: 0.5,
        ease: 'power2.out',
      },
      0
    )

    elementRef.current.querySelectorAll('.QuestionsLikert__Background svg').forEach((svg, index) => {
      const paths = svg.querySelectorAll('.QuestionsLikert__Background__Part')
      timeline.fromTo(
        paths,
        {
          scale: 0.01,
          transformOrigin: 'center',
        },
        {
          scale: 1,
          duration: 0.6,
          ease: 'back.out(1.7)',
          stagger: {
            amount: 0.3,
          },
        },
        index * 0.1
      )
    })

    timeline.add(() => setIsBackgroundEntering(false))
  }, [gsap, elementRef, container, buttonRef, setIsBackgroundEntering])

  // const onExit = useCallback(() => {

  // }, [gsap])

  return (
    <Transition
      nodeRef={elementRef}
      in={isVisible}
      onEnter={onEnter}
      // onExit={onExit}
      timeout={{
        enter: 900,
        exit: 500,
      }}
      mountOnEnter
      unmountOnExit
    >
      <div
        ref={elementRef}
        className={`QuestionsLikert relative w-full min-h-dvh md:h-screen inset-0 ${className ? className : ''}`}
      >
        <div className='QuestionsLikert__Background absolute top-0 left-0 w-full h-full pointer-events-none'>
          <svg
            className='absolute top-0 left-0 max-w-[50%] h-auto max-h-full text-secondary'
            viewBox='0 0 720 563'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            preserveAspectRatio='xMinYMin meet'
          >
            <path
              className='QuestionsLikert__Background__Part'
              d='M184.496 266.532C199.342 242.798 174.69 191.391 151.207 188.601C132.229 186.348 116.924 216.196 103.834 210.249C93.9056 205.737 100.321 187.477 87.6173 175.18C74.692 162.669 47.69 161.86 35.1227 174.315C18.7502 190.544 29.9177 226.287 44.0855 245.752C77.9347 292.263 165.762 296.486 184.496 266.532Z'
              fill='currentColor'
            />
            <path
              className='QuestionsLikert__Background__Part'
              d='M310.128 -118.483C293.051 -110.329 281.743 -94.961 269.109 -80.7955C259.771 -89.5865 250.43 -98.3071 240.43 -105.481C207.592 -126.426 205.354 -77.7351 198.182 -33.6522C178.634 -28.5615 157.943 -25.4304 139.55 -18.7101C109.987 -6.04478 131.412 16.1249 158.016 40.7801C148.55 51.5044 139.486 62.6292 130.215 73.5312C129.89 73.9109 129.573 74.3269 129.293 74.742C103.066 113.953 180.871 108.895 210.32 116.077C214.43 135.288 220.193 154.192 225.407 173.18C225.539 173.664 225.703 174.159 225.891 174.623C238 204.675 260.487 182.415 280.684 158.95C319.553 194.447 341.678 221.94 351.428 107.267C351.466 106.526 351.516 105.791 351.576 105.064C409.664 85.265 472.293 93.6318 400.421 28.6159C409.867 18.2146 418.474 7.33241 423.827 -5.33385C426.214 -10.985 424.252 -17.6369 418.949 -20.7256C398.203 -32.7985 373.099 -30.6722 349.668 -35.9679C342.119 -62.0489 340.215 -91.1972 325.598 -113.858C322.273 -119.013 315.673 -121.139 310.134 -118.495L310.128 -118.483Z'
              fill='currentColor'
            />
            <path
              className='QuestionsLikert__Background__Part'
              d='M455.561 123.368C455.144 124.398 454.729 125.426 454.312 126.456C453.637 128.587 454.162 130.605 454.89 132.594C456.122 135.959 457.56 139.24 458.859 142.577C459.726 144.806 460.721 146.974 461.692 149.149C463.591 153.403 465.747 157.533 468.08 161.566C469.264 163.613 470.52 165.609 471.904 167.524C472.888 168.886 474.048 170.024 475.603 170.709C476.299 170.991 476.996 171.273 477.693 171.554C480.238 172.497 482.636 171.791 485.002 170.85C487.246 169.957 489.31 168.701 491.431 167.558C492.928 166.75 494.457 166.002 495.982 165.249C498.418 164.045 500.847 162.83 503.289 161.636C505.075 160.764 506.873 159.91 508.646 159.011C512.074 157.275 515.612 155.752 518.885 153.714C520.052 152.986 521.139 152.154 522.147 151.213C522.777 150.625 523.055 149.816 523.517 149.123C523.883 148.217 524.249 147.311 524.615 146.405C525.475 144.065 524.728 141.879 523.987 139.696C523.231 137.473 522.323 135.311 521.264 133.219C520.085 130.895 518.907 128.572 517.743 126.241C516.904 124.563 516.098 122.866 515.272 121.181C513.882 118.345 512.565 115.475 511.186 112.633C510.704 111.638 510.352 110.582 509.88 109.581C509.078 107.886 508.307 106.178 507.158 104.666C506.439 103.719 505.548 103.054 504.499 102.556C504.364 102.491 504.192 102.487 504.112 102.328C503.497 102.079 502.884 101.831 502.268 101.583C501.409 101.433 500.612 101.014 499.715 101.001C497.761 100.973 495.91 101.462 494.103 102.148C491.783 103.028 489.61 104.221 487.45 105.433C485.908 106.298 484.322 107.08 482.756 107.897C480.466 109.094 478.158 110.258 475.86 111.44C474.132 112.329 472.397 113.207 470.669 114.096C468.254 115.341 465.833 116.574 463.439 117.861C462.02 118.625 460.6 119.393 459.159 120.116C457.649 120.874 456.294 121.771 455.559 123.373L455.561 123.368Z'
              fill='currentColor'
            />
          </svg>

          <SwitchTransition>
            <Transition
              key={`likert_number_${index}`}
              mountOnEnter
              unmountOnExit
              nodeRef={numberRef}
              timeout={{
                enter: 500,
                exit: 300,
              }}
            >
              {(state) => (
                <QuestionsLikertNumber
                  ref={numberRef}
                  index={index}
                  isLeaving={state === 'exiting'}
                  delay={isBackgroundEntering ? 0.5 : 0}
                />
              )}
            </Transition>
          </SwitchTransition>
        </div>

        <div ref={container} className='relative container w-full h-full content-center'>
          <div className='col-span-10 col-start-1 md:col-start-2 md:col-span-8 lg:col-start-3 lg:col-span-8 flex flex-col justify-center items-center min-h-dvh md:h-screen md:pb-100'>
            <SwitchTransition>
              <Transition
                key={`likert_instruction_${index}`}
                appear
                mountOnEnter
                unmountOnExit
                nodeRef={instructionRef}
                timeout={{
                  enter: 500,
                  exit: 300,
                }}
              >
                {(state) => (
                  <QuestionsLikertInstruction
                    ref={instructionRef}
                    question={question}
                    isLeaving={state === 'exiting'}
                    value={value}
                    setValue={setValue}
                    index={index}
                    max={max}
                  />
                )}
              </Transition>
            </SwitchTransition>
            <Pagination className='mt-64 md:-mb-144' current={index - 1} max={max} />

            <div className='QuestionsLikert__Footer fixed bottom-0 p-16 left-0 w-full flex justify-center items-center max-md:flex-col'>
              <div ref={buttonRef}>
                <Button disabled={!isVisible} onClick={onNext}>
                  {t(`runner.next`)}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  )
}
